import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Modal from '@/components/ui/Modal';
import { APIRoutes } from '@/services/api.routes';
import { MimeTypeIcon, type MimeType } from '@/components/ui/MimeTypeIcon';
import type { VideoAttachment } from '@/models';

interface EditVideoAttachmentModalProps {
  attachment: VideoAttachment;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EditVideoAttachmentModal: React.FC<EditVideoAttachmentModalProps> = ({
  attachment,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [filename, setFilename] = useState(attachment.filename);
  const [description, setDescription] = useState(attachment.description);
  const [order, setOrder] = useState(attachment.order);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!filename.trim()) {
      setError('Bitte geben Sie einen Dateinamen ein');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await APIRoutes.updateVideoAttachment(attachment.id, {
        filename: filename.trim(),
        description: description.trim() || undefined,
        order: order
      });

      onSuccess();
    } catch (err) {
      console.error('Error updating attachment:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setFilename(attachment.filename);
      setDescription(attachment.description);
      setOrder(attachment.order);
      setError(null);
      onClose();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Anhang bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-green-600" />}
      maxWidth="lg"
      closeDisabled={saving}
    >
      <form onSubmit={handleSubmit} className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* File Info */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center gap-3">
            <MimeTypeIcon mimeType={attachment.blob.mimeType as MimeType} className="w-6 h-6" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {attachment.blob.originalFilename}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatFileSize(attachment.blob.fileSizeBytes)} • {attachment.blob.mimeType}
              </p>
            </div>
          </div>
        </div>

        {/* Filename Input */}
        <div className="mb-4">
          <label htmlFor="filename" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Angezeigter Dateiname <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="filename"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Angezeigter Dateiname"
            required
          />
        </div>

        {/* Description Input */}
        <div className="mb-4">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Beschreibung (optional)
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Kurze Beschreibung des Anhangs"
          />
        </div>

        {/* Order Input */}
        <div className="mb-6">
          <label htmlFor="order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Reihenfolge
          </label>
          <input
            type="number"
            id="order"
            value={order}
            onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Reihenfolge (0 = erste Position)"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Niedrigere Zahlen werden zuerst angezeigt
          </p>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!filename.trim() || saving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Wird gespeichert...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                Speichern
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditVideoAttachmentModal;